'use client';

import React, { useState, useEffect } from 'react';
import { ShoppingCart, FileCheck, CheckCircle, X, HelpCircle } from 'lucide-react';

export function HowItWorks() {
  const [isOpen, setIsOpen] = useState(true);
  const [mounted, setMounted] = useState(false);

  // Load from localStorage on mount
  useEffect(() => {
    setMounted(true);
    const savedState = localStorage.getItem('howItWorksOpen');
    if (savedState !== null) {
      setIsOpen(JSON.parse(savedState));
    }
  }, []);

  // Save to localStorage when state changes
  const handleToggle = (newState: boolean) => {
    setIsOpen(newState);
    localStorage.setItem('howItWorksOpen', JSON.stringify(newState));
  };

  if (!mounted) return null;

  const steps = [
    {
      number: 1,
      title: 'Browse Menu',
      description: 'Explore delicious menu items curated for your event',
      icon: ShoppingCart,
    },
    {
      number: 2,
      title: 'Share Your Details',
      description: 'Tell us about your event, guest count, and preferred dates',
      icon: FileCheck,
    },
    {
      number: 3,
      title: 'Get Quotation',
      description: 'Vendor reviews your request and sends you a custom quote',
      icon: CheckCircle,
    },
  ];

  // Expanded view
  if (isOpen) {
    return (
      <div className="mx-2 sm:mx-4 mt-8">
        <div className="bg-green-600 rounded-2xl p-3 md:p-8 relative">
          {/* Close button */}
          <button
            onClick={() => handleToggle(false)}
            className="absolute top-4 right-4 p-1 hover:bg-green-700 rounded-lg transition-colors"
            aria-label="Close how it works"
          >
            <X className="w-5 h-5 text-white hover:text-gray-100" />
          </button>

          <h2 className="text-lg md:text-3xl font-bold text-center mb-1 md:mb-2 text-white">How does this work?</h2>
          <p className="text-center text-green-100 mb-4 md:mb-8 text-xs md:text-base px-1">
            Get a custom quotation for your event in just 3 steps
          </p>

          <div className="grid grid-cols-3 gap-3 md:gap-6">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={step.number} className="flex flex-col items-center">
                  {/* Step Container */}
                  <div className="relative w-full mb-4">
                    {/* Connector line */}
                    {index < steps.length - 1 && (
                      <div className="absolute top-8 md:top-12 -right-1/2 w-full h-1 bg-green-300" />
                    )}

                    {/* Circle with number and icon */}
                    <div className="flex flex-col items-center">
                      <div className="relative w-16 h-16 md:w-28 md:h-28 mb-2 md:mb-4">
                        <div className="absolute inset-0 bg-white rounded-full flex items-center justify-center">
                          <Icon className="w-7 h-7 md:w-12 md:h-12 text-green-600" />
                        </div>
                        <span className="absolute -top-1 -right-1 md:-top-2 md:-right-2 bg-green-700 text-white text-xs md:text-sm font-bold w-6 h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center">
                          {step.number}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Step Info */}
                  <h3 className="text-xs md:text-lg font-semibold text-center mb-1 md:mb-2 text-white">
                    {step.title}
                  </h3>
                  <p className="hidden md:block text-sm text-green-100 text-center">
                    {step.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Collapsed view
  return (
    <div className="mx-2 sm:mx-4 my-4">
      <button
        onClick={() => handleToggle(true)}
        className="w-full bg-white rounded-lg p-4 hover:shadow-md transition-shadow flex items-center justify-center gap-2 text-pink-600 font-semibold"
      >
        <HelpCircle className="w-5 h-5" />
        Learn How It Works
      </button>
    </div>
  );
}
